import { Box, Group, Stack, Text } from "@mantine/core";
import { IconCircleCheck } from "@tabler/icons-react";
import clsx from "clsx";
import { Fragment } from "react";

import type { NarrativeBeat } from "../types";

import { NarrativeCardActions } from "../NarrativeCardActions/NarrativeCardActions";
import { beatHasGenerated, beatHasVerbatim } from "../types";
import styles from "./NarrativeBeatCard.module.css";

/**
 * How a beat card presents. Maps 1:1 to the four visual renderings:
 * - `active`   — the beat being worked; controls shown below the card.
 * - `preview`  — the next beat, greyed, real content, no controls.
 * - `covered`  — history: read-marking wash + "You did it!".
 * - `skipped`  — history: greyed, "Skipped".
 */
export type BeatCardState = "active" | "preview" | "covered" | "skipped";

export interface NarrativeBeatCardProps {
    beat: NarrativeBeat;
    state: BeatCardState;
    /** Cue names for the evidence chip, snapshotted from the global cue store. Omit/empty to hide. */
    cues?: string[];
    /** Called when the expert marks the beat read. Only wired when `state === "active"`. */
    onRead?: () => void;
    /** Called when the expert skips the beat. Only offered when the beat has no verbatim content. */
    onSkip?: () => void;
    /** Suppress the active read/skip controls — used when a form card (Guide/Enroll) drives the step. */
    hideControls?: boolean;
}

const EvidenceChip = ({ cues }: { cues?: string[] }) => {
    if (!cues?.length) return null;
    return (
        <Text component="p" className={styles.chip}>
            I heard{" "}
            {cues.map((cue, i) => (
                <span key={cue}>
                    {i > 0 ? ", " : ""}
                    <span className={styles.chipCue}>{cue}</span>
                </span>
            ))}
        </Text>
    );
};

export const NarrativeBeatCard = ({ beat, state, cues, onRead, onSkip, hideControls }: NarrativeBeatCardProps) => {
    const hasVerbatim = beatHasVerbatim(beat);
    const hasGenerated = beatHasGenerated(beat);
    const skippable = !hasVerbatim;
    const covered = state === "covered";
    // One bullet per segment instead of one flowing paragraph. The type landed in #7180 but its
    // renderer was written on a branch that never merged, so this was inert on main until now.
    const isBullets = beat.layout === "bullets";
    // Segments render inline as one paragraph, but each verbatim run is highlighted so the expert can
    // see exactly which spans are byte-exact locked copy. The beat-level eyebrow, quote glyph, and
    // skippability still derive from whether ANY segment is verbatim — but they no longer imply the
    // WHOLE beat is verbatim: a mixed beat weaves locked spans through otherwise-generated prose.
    // The evidence chip ("I heard <cue>") gates on this beat carrying generated (cue-derived) prose,
    // NOT on cues merely existing for the session: `cues` is a session-global snapshot passed to every
    // card, so an unconditional chip would assert cue-derived provenance under a PURE-verbatim beat
    // (100% locked copy that by construction was not derived from cues). Gating on `hasGenerated`
    // keeps the chip on mixed beats (which do have cue-derived prose) and hides it on pure-verbatim
    // ones. EvidenceChip also early-returns null when the session has no cues at all.

    return (
        <Stack gap={0} className={clsx(covered && styles.covered, state === "skipped" && styles.skipped)}>
            <Box
                className={clsx(
                    styles.card,
                    hasVerbatim ? styles.verbatimCard : styles.suggestedCard,
                    state === "preview" && styles.preview,
                )}
            >
                <Group gap={6} className={hasVerbatim ? styles.eyebrowRow : styles.eyebrowRowSuggested}>
                    <span
                        className={clsx(styles.eyebrow, hasVerbatim ? styles.eyebrowVerbatim : styles.eyebrowSuggested)}
                    >
                        {hasVerbatim ? "Read verbatim" : "Suggested script"}
                    </span>
                </Group>

                <Box className={hasVerbatim ? undefined : styles.quoteBlock}>
                    {isBullets ? (
                        <Stack
                            component="ul"
                            gap={8}
                            className={clsx(styles.bulletList, styles.wash, covered && styles.washOn)}
                        >
                            {beat.segments.map((segment, i) => (
                                <li key={i} className={styles.bulletItem}>
                                    {segment.kind === "verbatim" ? (
                                        <span className={styles.verbatimSpan}>{segment.text}</span>
                                    ) : (
                                        segment.text
                                    )}
                                </li>
                            ))}
                        </Stack>
                    ) : (
                        <Text component="p" my={0} className={clsx(styles.body, hasVerbatim && styles.verbatimBody)}>
                            {hasVerbatim && <span className={styles.quoteGlyph}>&ldquo;</span>}
                            <span className={clsx(styles.wash, covered && styles.washOn)}>
                                {beat.segments.map((segment, i) => (
                                    <Fragment key={i}>
                                        {i > 0 ? " " : ""}
                                        {segment.kind === "verbatim" ? (
                                            <span className={styles.verbatimSpan}>{segment.text}</span>
                                        ) : (
                                            segment.text
                                        )}
                                    </Fragment>
                                ))}
                            </span>
                        </Text>
                    )}
                    {hasGenerated && <EvidenceChip cues={cues} />}
                </Box>

                {state === "active" && !hideControls && (
                    <NarrativeCardActions>
                        {skippable && <NarrativeCardActions.Link onClick={onSkip}>Skip it</NarrativeCardActions.Link>}
                        <NarrativeCardActions.Primary onClick={onRead}>I read it</NarrativeCardActions.Primary>
                    </NarrativeCardActions>
                )}

                {/* Status rows share the action row's slot — inside the card, bottom-left — so an
                    active card and its completed/skipped state stay visually consistent. */}
                {covered && (
                    <Group justify="flex-start" align="center" gap="md" className={styles.controls}>
                        <span className={styles.doneStatus}>
                            You did it!
                            <IconCircleCheck size={18} stroke={2} color="#1dc28c" />
                        </span>
                    </Group>
                )}

                {state === "skipped" && (
                    <Group justify="flex-start" align="center" gap="md" className={styles.controls}>
                        <span className={styles.skippedStatus}>Skipped</span>
                    </Group>
                )}
            </Box>
        </Stack>
    );
};
